import {
  ISplitContentWithTextFields,
  IPictogramFields
} from '@/types/contentful';
import classNames from 'classnames';
import { Asset } from '../AtomicComponents/Asset';
import { Eyebrow } from '../Eyebrow';
import RichText from '../RichText';
import { BLOCKS } from '@contentful/rich-text-types';
import type { Node as RichTextNode } from '@contentful/rich-text-types';
import PictogramComponent from '../AtomicComponents/Pictogram';
import { TextLink } from '../AtomicComponents/TextLink';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import logger from '@greenlightme/logger';
import axios from 'axios';
import { useState, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSpring, animated } from 'react-spring';

export function SplitContentWithText({
  assetFirstToggle,
  roundedCorners,
  colorTheme,
  invertColors,
  asset,
  videoImageBackup,
  eyebrow,
  headerText,
  splitContentFullTextParagraph,
  splitContentBullets,
  textLinkCtaText,
  textLinkCtaAnchor,
  assetDecoration,
  assetDecorationPosition
}: ISplitContentWithTextFields) {
  const SplitContentColorTheme = {
    piggybank: {
      background: invertColors ? 'bg-accent5-weakest' : 'bg-accent5-strongest',
      header: invertColors ? 'text-accent5-strongest' : 'text-piggybank-400',
      text: invertColors
        ? 'text-on-screen-surface'
        : 'text-on-screen-surface-dark',
      arrow: invertColors ? 'dark' : 'light',
      pictogramColor: 'accent-5'
    },
    lagoon: {
      background: invertColors ? 'bg-accent2-weakest' : 'bg-accent2-strongest',
      header: invertColors ? 'text-accent2-strongest' : 'text-accent2-base',
      text: invertColors
        ? 'text-on-screen-surface'
        : 'text-on-screen-surface-dark',
      arrow: invertColors ? 'dark' : 'light',
      pictogramColor: 'accent-2'
    },
    grape: {
      background: invertColors ? 'bg-accent1-weakest' : 'bg-accent1-strongest',
      header: invertColors ? 'text-accent1-strongest' : 'text-accent1-base',
      text: invertColors
        ? 'text-on-screen-surface'
        : 'text-on-screen-surface-dark',
      arrow: invertColors ? 'dark' : 'light',
      pictogramColor: 'accent-1'
    },
    tangerine: {
      background: invertColors ? 'bg-accent4-weakest' : 'bg-accent4-strongest',
      header: invertColors ? 'text-accent4-strongest' : 'text-accent4-base',
      text: invertColors
        ? 'text-on-screen-surface'
        : 'text-on-screen-surface-dark',
      arrow: invertColors ? 'dark' : 'light',
      pictogramColor: 'accent-4'
    },
    lime: {
      background: invertColors ? 'bg-accent3-weakest' : 'bg-accent3-strongest',
      header: invertColors ? 'text-accent3-strongest' : 'text-accent3-base',
      text: invertColors
        ? 'text-on-screen-surface'
        : 'text-on-screen-surface-dark',
      arrow: invertColors ? 'dark' : 'light',
      pictogramColor: 'accent-3'
    },
    brand: {
      background: invertColors ? 'bg-brand-weakest' : 'bg-brand-strongest',
      header: invertColors ? 'text-brand-strongest' : 'text-brand-base',
      text: invertColors
        ? 'text-on-screen-surface'
        : 'text-on-screen-surface-dark',
      arrow: invertColors ? 'dark' : 'light',
      pictogramColor: 'brand'
    }
  };

  const colorThemeKey = (
    Object.prototype.hasOwnProperty.call(SplitContentColorTheme, colorTheme)
      ? colorTheme
      : 'lagoon'
  ) as keyof typeof SplitContentColorTheme;
  const [dotLottieData, setDotLottieData] = useState<ArrayBuffer | null>(null);
  const [dotLottieInstance, setDotLottieInstance] = useState<{
    play: () => void;
    stop: () => void;
  } | null>(null);
  const [entranceDone, setEntranceDone] = useState(false);
  const [lottieKey, setLottieKey] = useState(0);

  // Add useInView hook for the main container with 50% threshold
  const [containerRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.5
  });

  // Handle Contentful URL normalization
  const rawUrl = assetDecoration?.fields?.file?.url;
  const url = useMemo(() => {
    if (!rawUrl) return undefined;
    // Contentful asset URLs are protocol-relative: //images.ctfassets.net/...
    return rawUrl.startsWith('http') ? rawUrl : `https:${rawUrl}`;
  }, [rawUrl]);

  // DotLottie files are self-contained; no external asset path or JSON aspect is needed
  const isDotLottie =
    assetDecoration?.fields?.file?.contentType?.includes('zip') ||
    (url && url.toLowerCase().endsWith('.lottie'));

  // Fetch .lottie binary only when in view
  useEffect(() => {
    if (!inView) return;
    if (!url || dotLottieData) return;
    if (!isDotLottie) return;

    axios
      .get(url, { responseType: 'arraybuffer' })
      .then(({ data }) => setDotLottieData(data))
      .catch((err) => {
        logger.error('Error fetching .lottie data', err);
        console.error('Error fetching .lottie data', err);
      });
  }, [assetDecoration?.fields?.file?.contentType, url, dotLottieData, inView]);

  // Stop playback if not in view (in case it could toggle elsewhere)
  useEffect(() => {
    if (!dotLottieInstance) return;
    if (!inView) {
      dotLottieInstance.stop();
    }
  }, [inView, dotLottieInstance]);

  // After entrance animation completes, delay 400ms then start playback
  useEffect(() => {
    if (!dotLottieInstance) return;
    if (!entranceDone) return;
    const timeoutId = setTimeout(() => {
      dotLottieInstance.play();
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [entranceDone, dotLottieInstance]);

  // Handle window resize for Lottie re-rendering
  useEffect(() => {
    if (!isDotLottie) return;

    const handleResize = () => {
      setLottieKey((prev) => prev + 1);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isDotLottie]);

  // Re-render Lottie when key props change
  useEffect(() => {
    if (!isDotLottie) return;
    setLottieKey((prev) => prev + 1);
  }, [assetDecorationPosition, assetFirstToggle, isDotLottie]);

  // Animation for assetDecoration (both Asset and Lottie)
  const alignTuple: [number, number] =
    assetDecorationPosition === 'top left'
      ? [0, 0]
      : assetDecorationPosition === 'top right'
      ? [1, 0]
      : assetDecorationPosition === 'bottom left'
      ? [0, 1]
      : assetDecorationPosition === 'bottom right'
      ? [1, 1]
      : [0, 0];
  const decorationAnimation = useSpring(
    isDotLottie
      ? {
          transform: inView ? 'scale(1)' : 'scale(0.8)',
          opacity: inView ? 1 : 0,
          config: { tension: 240, friction: 30, clamp: true },
          onRest: () => {
            if (inView && isDotLottie) setEntranceDone(true);
          }
        }
      : {
          transform: inView ? 'scale(1)' : 'scale(0)',
          opacity: inView ? 1 : 0,
          config: { tension: 240, friction: 30, clamp: true }
        }
  );

  const isBulletMode = Boolean(
    splitContentBullets && !splitContentFullTextParagraph
  );

  return (
    <div className="block-container" ref={containerRef}>
      <div
        className={classNames(
          'layout layout-padding flex min-h-[516px] w-full !max-w-none !items-stretch justify-center gap-4 tablet:gap-6 desktop:!flex-nowrap',
          assetFirstToggle
            ? 'flex-col desktop:flex-row'
            : 'flex-col-reverse desktop:flex-row-reverse'
        )}
      >
        <div
          className={classNames(
            'relative flex w-full flex-col gap-2 overflow-hidden object-cover desktop:h-full',
            // In text mode (splitContentFullTextParagraph present), image is 7/12
            // In bullet mode (splitContentBullets present), image is 5/12
            splitContentFullTextParagraph
              ? 'desktop:basis-7/12'
              : 'desktop:basis-5/12',
            roundedCorners ? 'rounded-card-shape max:rounded-lg' : ''
          )}
        >
          {asset && (
            <Asset
              asset={asset}
              videoImageBackup={videoImageBackup}
              className="h-full [&>img]:size-full [&>img]:object-cover [&>video]:size-full [&>video]:object-cover"
            />
          )}
          {assetDecoration && assetDecorationPosition && (
            <animated.div
              style={decorationAnimation}
              className={classNames(
                'absolute flex p-6 tablet:p-10 max:p-16',
                assetDecorationPosition === 'top left' &&
                  'left-0 top-0 origin-top-left !pb-0 !pr-0 [&>*]:mr-auto',
                assetDecorationPosition === 'top right' &&
                  'right-0 top-0 origin-top-right !pb-0 !pl-0 [&>*]:ml-auto',
                assetDecorationPosition === 'bottom left' &&
                  'bottom-0 left-0 origin-bottom-left items-end justify-end !pr-0 !pt-0 [&>*]:mr-auto [&>*]:mt-auto',
                assetDecorationPosition === 'bottom right' &&
                  'bottom-0 right-0 origin-bottom-right !pl-0 !pt-0 [&>*]:ml-auto [&>*]:mt-auto',
                '!w-1/2',
                assetDecoration.fields.file?.contentType === 'application/zip'
                  ? classNames(
                      'h-full [&>*]:!h-full [&>*]:!w-full [&>*]:min-w-[120px] [&>*]:max-w-[180px] tablet:[&>*]:min-w-[200px] tablet:[&>*]:max-w-60 desktop:[&>*]:min-w-40 desktop:[&>*]:max-w-[200px]',
                      isBulletMode && 'desktop:[&>*]:!w-full'
                    )
                  : '[&>*]:!min-w-[120px] [&>*]:!max-w-[180px] tablet:[&>*]:!min-w-[200px] tablet:[&>*]:!max-w-[240px] desktop:[&>*]:!min-w-[160px] desktop:[&>*]:!max-w-[200px]'
              )}
            >
              {isDotLottie ? (
                <div
                  data-testid="split-content-lottie"
                  className={classNames('rounded-inherit')}
                >
                  {dotLottieData && (
                    <DotLottieReact
                      key={lottieKey}
                      data={dotLottieData}
                      loop={false}
                      className={classNames(
                        'flex h-full',
                        assetDecorationPosition === 'top left' &&
                          'items-start justify-start',
                        assetDecorationPosition === 'top right' &&
                          'items-start justify-end',
                        assetDecorationPosition === 'bottom left' &&
                          'items-end justify-start',
                        assetDecorationPosition === 'bottom right' &&
                          'items-end justify-end'
                      )}
                      autoplay={false}
                      layout={{ align: alignTuple }}
                      dotLottieRefCallback={(instance: {
                        play: () => void;
                        stop: () => void;
                      }) => setDotLottieInstance(instance)}
                    />
                  )}
                </div>
              ) : (
                <Asset asset={assetDecoration} />
              )}
            </animated.div>
          )}
        </div>
        <div
          className={classNames(
            'flex w-full max-w-none flex-col items-center justify-center gap-6 self-stretch px-6 py-10 desktop:px-10 desktop:py-16 max:px-16 max:py-25',
            // In text mode (splitContentFullTextParagraph present), content is 5/12
            // In bullet mode (splitContentBullets present), content is 7/12
            splitContentFullTextParagraph
              ? 'min-h-[412px] desktop:basis-5/12'
              : 'min-h-[468px] desktop:basis-7/12',
            roundedCorners ? 'rounded-card-shape max:rounded-lg' : '',
            SplitContentColorTheme[colorThemeKey]?.background
          )}
        >
          <div className="mx-auto flex w-full flex-col items-start gap-4 self-stretch tablet:max-w-[600px] desktop:mx-auto desktop:max-w-[900px] ">
            {eyebrow && (
              <Eyebrow
                eyebrowFields={eyebrow.fields}
                className={classNames(
                  '-mb-4 [&_p]:font-normal',
                  `${SplitContentColorTheme[colorThemeKey]?.text}`
                )}
              />
            )}
            <h3
              className={classNames(
                'typography-display-3',
                SplitContentColorTheme[colorThemeKey]?.header
              )}
            >
              {headerText}
            </h3>
            {splitContentFullTextParagraph && (
              <RichText
                data={splitContentFullTextParagraph}
                overrideOptions={{
                  [BLOCKS.PARAGRAPH]: (_node: RichTextNode, children) => {
                    return (
                      <p
                        className={classNames(
                          'typography-body-1 font-normal',
                          SplitContentColorTheme[colorThemeKey]?.text
                        )}
                      >
                        {children}
                      </p>
                    );
                  }
                }}
              />
            )}
            {splitContentBullets && (
              <div className="flex flex-col items-start gap-6 self-stretch pt-2 desktop:flex-row desktop:gap-4 desktop:pt-3">
                {splitContentBullets.map((bullet, index) => (
                  <div
                    key={index}
                    className={
                      'flex flex-1 flex-row items-start gap-4 desktop:flex-col'
                    }
                  >
                    <div className="size-8">
                      {bullet.fields.pictogram && (
                        <PictogramComponent
                          size={'sm'}
                          type={
                            SplitContentColorTheme[colorThemeKey]
                              ?.pictogramColor as IPictogramFields['type']
                          }
                          shape={bullet.fields.pictogram.fields.shape}
                          icon={bullet.fields.pictogram.fields.icon}
                          inverse={invertColors}
                        />
                      )}
                    </div>
                    <p
                      className={classNames(
                        'typography-body-1',
                        SplitContentColorTheme[colorThemeKey]?.text
                      )}
                    >
                      {bullet.fields.iconCopy}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {textLinkCtaText && textLinkCtaAnchor && (
              <div className="flex h-10 w-full flex-grow flex-col items-start justify-center">
                <TextLink
                  href={textLinkCtaAnchor}
                  showArrow={true}
                  arrowColor={SplitContentColorTheme[colorThemeKey]?.arrow}
                  className={classNames(
                    'flex items-center',
                    SplitContentColorTheme[colorThemeKey]?.text
                  )}
                >
                  {textLinkCtaText}
                </TextLink>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
